import { useEffect, useState } from 'react';
import {
  apiErrorText,
  useListContractEstimatesQuery,
  useUploadKsActMutation,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import { estimateVersionLabel } from '../contractEstimateLabels';
import { Err, FileHint, ObjName, SubmitWrapper } from './styled';

interface ActUploadCardProps {
  contractId: string;
  canWrite: boolean;
  onUploaded: (actId: string) => void;
}

export function ActUploadCard({ contractId, canWrite, onUploaded }: ActUploadCardProps) {
  const [actFile, setActFile] = useState<File | null>(null);
  const [bindEstimateId, setBindEstimateId] = useState('');
  const [actError, setActError] = useState<string | null>(null);
  const [actWarnings, setActWarnings] = useState<string[]>([]);

  const estimatesQ = useListContractEstimatesQuery(
    { contractId: contractId.trim() },
    { skip: contractId.trim().length < 8 },
  );
  const [uploadAct, { isLoading: uploadingAct }] = useUploadKsActMutation();

  const estimates = estimatesQ.data?.items ?? [];

  // Сбросить выбранную смету, если она пропала из списка (сменили UUID договора
  // или версия устарела) — иначе select покажет «без привязки», а в state
  // останется старый id → молчаливая привязка к эталону другого договора.
  useEffect(() => {
    if (bindEstimateId && !estimates.some((es) => es.id === bindEstimateId)) {
      setBindEstimateId('');
    }
  }, [estimates, bindEstimateId]);

  async function handleUploadAct(e: React.FormEvent) {
    e.preventDefault();
    setActError(null);
    setActWarnings([]);
    if (!actFile) {
      setActError('Приложите файл пакета «выполнение» (xlsx).');
      return;
    }
    const fd = new FormData();
    fd.append('file', actFile);
    // Привязываем только если выбранная смета всё ещё есть в списке текущего
    // договора — иначе можно молча привязать акт к эталону другого договора.
    const validBind = estimates.some((es) => es.id === bindEstimateId) ? bindEstimateId : '';
    if (validBind) fd.append('contract_estimate_id', validBind);
    try {
      const res = await uploadAct(fd).unwrap();
      setActFile(null);
      setActWarnings(res.warnings ?? []);
      if (res.act_ids.length > 0) onUploaded(res.act_ids[0]);
    } catch (err) {
      setActError(apiErrorText(err, 'Не удалось загрузить пакет КС. Проверьте формат файла.'));
      // eslint-disable-next-line no-console
      console.error('uploadKsAct failed', err);
    }
  }

  return (
    <Card>
      <form onSubmit={handleUploadAct}>
        <ObjName>2. Акт КС-2 / КС-3 (пакет «выполнение»)</ObjName>
        <Field>
          <Label>Файл пакета</Label>
          <Input
            type="file"
            accept=".xlsx,.xlsm"
            onChange={(e) => setActFile(e.target.files?.[0] ?? null)}
          />
          <FileHint>Excel-пакет: листы КС-3 + акты КС-2.</FileHint>
        </Field>
        <Field>
          <Label>Привязать договорную смету</Label>
          <Select
            value={estimates.some((es) => es.id === bindEstimateId) ? bindEstimateId : ''}
            onChange={(e) => setBindEstimateId(e.target.value)}
            disabled={estimates.length === 0}
          >
            <option value="">— без привязки (привязать позже нельзя) —</option>
            {estimates.map((es) => (
              <option key={es.id} value={es.id}>
                {estimateVersionLabel(es)}
              </option>
            ))}
          </Select>
          <FileHint>
            {estimates.length === 0
              ? 'Сначала укажите UUID договора и загрузите эталон слева.'
              : 'Анализ возможен только при привязанной смете.'}
          </FileHint>
        </Field>
        {actError && <Err>{actError}</Err>}
        <ParseWarningsBanner warnings={actWarnings} />
        <SubmitWrapper>
          <Button
            type="submit"
            $variant="primary"
            disabled={uploadingAct || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {uploadingAct ? 'Загрузка…' : 'Загрузить акт'}
          </Button>
        </SubmitWrapper>
      </form>
    </Card>
  );
}
